import type { ResourceResponse } from "types";
import { useTypedSelector, useSimpleRequest } from "hooks";
import { BooleanSwitchValue } from "consts/general";
import { BLACKLIST_CONSUMER_REASON } from "consts/consumers";
import { SetupIdentifier } from "consts/setups";
import { compareIds } from "util/support";
import {
    createConsumersBlacklist,
    deleteConsumersBlacklist,
    updateConsumer,
    updateOrCreateConsumerEmails
} from "features/consumers/api";
import type { BlacklistedConsumer, ConsumerEmailEntity } from "features/consumers/types";
import { selectSetupsSlice } from "features/setups/selectors";
import type { OptionsProps } from "../types";

export default function useOptionActions({
    duplicatedEmailRegistryEntry,
    tabRegistry: [
        manageDuplicatesRegistry,
        updateManageDuplicatesRegistry
    ]
}: OptionsProps) {
    const domains = useTypedSelector(selectSetupsSlice(SetupIdentifier.Mors));
    const request = useSimpleRequest();

    const handleRemoveDuplicatedEmail = ({ data }: ResourceResponse<ConsumerEmailEntity>) =>
        updateManageDuplicatesRegistry(
            manageDuplicatesRegistry.filter(manageDuplicatesRegistryEntity =>
                manageDuplicatesRegistryEntity.email !== data.email
            )
        );

    const handleBlacklistConsumer = () =>
        updateManageDuplicatesRegistry(
            manageDuplicatesRegistry.map(manageDuplicatesRegistryEntity =>
                compareIds(duplicatedEmailRegistryEntry.id, manageDuplicatesRegistryEntity.id)
                    ? manageDuplicatesRegistryEntity.blacklist()
                    : manageDuplicatesRegistryEntity
            )
        );

    const handleWhitelistConsumer = () =>
        updateManageDuplicatesRegistry(
            manageDuplicatesRegistry.map(manageDuplicatesRegistryEntity =>
                compareIds(duplicatedEmailRegistryEntry.id, manageDuplicatesRegistryEntity.id)
                    ? manageDuplicatesRegistryEntity.whitelist()
                    : manageDuplicatesRegistryEntity
            )
        );


    const blacklistConsumer = () => {
        const computedEmail = (duplicatedEmailRegistryEntry.email
            ? {
                email: duplicatedEmailRegistryEntry.email
            }
            : {
                hashedEmail: duplicatedEmailRegistryEntry.hashedEmail
            }) as Partial<BlacklistedConsumer>;

        return request(() => createConsumersBlacklist({
            ...computedEmail,
            importedFrom: BLACKLIST_CONSUMER_REASON
        }), {
            onSuccess: handleBlacklistConsumer,
            shouldNotifyOnSuccess: true
        });
    };

    const whitelistConsumer = () => new Promise<void>((resolve, reject) =>
        request(() =>
            deleteConsumersBlacklist(
                duplicatedEmailRegistryEntry.computedEmail
            ), {
            onSuccess: () => request(() => updateConsumer({
                coreId: duplicatedEmailRegistryEntry.computedEmail,
                isRehabilitated: true,
                active: BooleanSwitchValue.On,
                email: duplicatedEmailRegistryEntry.computedEmail,
                MOR: domains.MOR
            }), {
                onSuccess: () => {
                    handleWhitelistConsumer();
                    resolve();
                },
                onError: reject,
                shouldNotifyOnSuccess: true
            }),
            onError: reject
        }));

    const toggleBlacklist = () =>
        duplicatedEmailRegistryEntry.isBlacklisted
            ? whitelistConsumer()
            : blacklistConsumer();

    const removeDuplicate = () => request<ConsumerEmailEntity>(
        () => updateOrCreateConsumerEmails({
            coreId: duplicatedEmailRegistryEntry.id,
            samePersonIdentifier: ''
        }), {
        onSuccess: handleRemoveDuplicatedEmail,
        shouldNotifyOnSuccess: true
    });

    return {
        toggleBlacklist,
        removeDuplicate
    };
};
